/**
 * Peer Input Access Notifier
 *
 * Bridges the coordinator-layer `PeerPermissions` (host-side enforcement) to the
 * player client by sending a `setInputAccess` message over the peer data channel.
 *
 * The coordinator path (`InputManager`) gates inbound input purely on
 * `PeerPermissions`, but that only drops disallowed input host-side; it does not
 * tell the client what it is allowed to do. Clients (e.g. marketing-demo
 * quickstart) need to know their granted access so they bind/unbind the matching
 * capture listeners (notably touch). This notifier is that outbound signal.
 *
 * `PeerPermissions` has no `chat` field, while the wire type
 * `PlaycastPlayerInputAccess` does. Chat is not gated by campaign input support
 * in the coordinator path, so it is always granted (legacy default).
 */

import type { PlaycastPlayerInputAccess } from '@playcastdotio/CoreTypes';
import { generators } from '@playcastdotio/Messaging';
import type { PeerCoordinator } from './PeerCoordinator';
import type { PeerPermissions } from './types';

/**
 * Map coordinator `PeerPermissions` to the wire `PlaycastPlayerInputAccess`,
 * filling the `chat` field (always granted) that the coordinator layer omits.
 */
export const peerPermissionsToInputAccess = (permissions: PeerPermissions): PlaycastPlayerInputAccess => ({
  gamepad: permissions.gamepad,
  keyboard: permissions.keyboard,
  mouse: permissions.mouse,
  touch: permissions.touch,
  chat: true,
});

export type PeerInputAccessNotifierDeps = {
  peerCoordinator: PeerCoordinator;
  getHostAuthId: () => string;
};

export class PeerInputAccessNotifier {
  constructor(private readonly deps: PeerInputAccessNotifierDeps) {}

  /**
   * Send the player's current granted access to a single peer.
   */
  public notifyPeer(peerId: string, permissions: PeerPermissions): void {
    this.deps.peerCoordinator.sendToPeer(peerId, this.encode(permissions));
  }

  /**
   * Send the same granted access to every connected player peer.
   */
  public notifyAllPlayers(permissions: PeerPermissions): void {
    for (const peer of this.deps.peerCoordinator.getPeersByType('player')) {
      this.deps.peerCoordinator.sendToPeer(peer.peerId, this.encode(permissions));
    }
  }

  private encode(permissions: PeerPermissions): string {
    const access = peerPermissionsToInputAccess(permissions);
    const message = generators.player.setInputAccess(this.deps.getHostAuthId(), access);
    return JSON.stringify(message);
  }
}
